// .kiro/specs/1600-cognito-firestore-auth — signs the web client into Firebase
// right after Cognito login (or on app load if already logged in), and out the
// moment the Cognito session disappears. `cognitoLogin`/`cognitoLogout` live in
// the SHARED `libs/redux` (also used by `feel`/`payments`, which don't talk to
// Firestore) — bolting a Firebase dependency onto those thunks would leak an
// app-specific concern into apps that don't need it. Deriving this from
// `state.auth.data.userId` instead mirrors how every other chat hook in this
// app already reacts to auth state (see useUnreadMessages.js).
//
// This hook alone does not enforce anything — the actual access control is
// firestore.rules (EARS 2). A user who never gets signed in here just keeps
// failing every Firestore rule check, same as any other unauthenticated
// client; nothing here needs to gate the app's own Firestore calls directly.
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { getFirebaseTokenApiCall } from '@guryou/api-client-gql';
import { getAuth, signInWithCustomToken, signOut } from 'firebase/auth';

import { app } from './firebase';
import { clearPushToken } from './push';

const useFirebaseAuthBridge = () => {
  const userId = useSelector(state => state?.auth?.data?.userId);
  const signedInUidRef = useRef(null);

  useEffect(() => {
    const auth = getAuth(app);

    if (!userId) {
      if (signedInUidRef.current) {
        const previousUid = signedInUidRef.current;
        signedInUidRef.current = null;
        // An FCM token is registered per BROWSER/device, not per logged-in
        // user — left alone, a message to the outgoing user would still
        // reach this exact device's OS notification tray after someone else
        // logs in on it. Must run BEFORE signOut(auth): removing the token
        // from deviceTokens/{userId} needs request.auth.uid === userId,
        // still true only while this session is live.
        clearPushToken(previousUid).catch(() => null);
        signOut(auth).catch(() => null);
      }
      return;
    }

    if (signedInUidRef.current === String(userId)) {
      return;
    }

    // apiCall() (libs/api-client-gql/src/generated/gqlClient.js) resolves to
    // { success, data } / { success: false, errors } — NOT the raw payload
    // directly. Destructuring `{ firebaseToken }` straight off the promise
    // (an earlier bug here) always yields undefined, which
    // signInWithCustomToken happily forwards to Firebase as an empty token,
    // surfacing as `auth/missing-custom-token` client-side — silent because
    // the whole chain is best-effort (.catch(() => null) below).
    getFirebaseTokenApiCall()
      .then(result => {
        if (!result?.success || !result.data?.firebaseToken) {
          // eslint-disable-next-line no-console
          console.error('[firebase-auth-bridge] getFirebaseToken failed', result);
          throw new Error('getFirebaseToken failed');
        }
        return signInWithCustomToken(auth, result.data.firebaseToken);
      })
      .then(() => {
        signedInUidRef.current = String(userId);
      })
      // Best-effort: a failed sign-in just leaves Firestore calls failing the
      // rules check until the next userId change / app reload retries this —
      // never crash the app shell over it. Logged (not swallowed silently)
      // so a stuck-loading symptom is diagnosable from the console alone.
      .catch(err => {
        // eslint-disable-next-line no-console
        console.error('[firebase-auth-bridge] sign-in failed', err);
      });
  }, [userId]);
};

export default useFirebaseAuthBridge;
